import { QueryClient } from '@tanstack/react-query';
import { T } from '@tolgee/react';
import { Tooltip } from 'antd';
import { uniq } from 'lodash';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import CapSvg from '../../../../assets/icons/CapSvg';
import ClockSvg from '../../../../assets/icons/ClockSvg';
import CuripodAIStarsSvg from '../../../../assets/icons/CuripodAIStarsSvg';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import { RightAngleSvg } from '../../../../assets/icons/RightAngleSvg';
import ShareFilledSvg from '../../../../assets/icons/ShareFilledSvg';
import theme from '../../../../assets/theme';
import { Button } from '../../../../components/_atoms/Button';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import BaseModal from '../../../../components/_molecules/BaseModal';
import { NewLandingPageFooter } from '../../../../components/_molecules/LandingPageFooter';
import ProgressBarV2 from '../../../../components/_molecules/ProgressBar/ProgressBarV2';
import ShareGlobalLesson from '../../../../components/_molecules/ShareGlobalLesson';
import {
  GeneratorFormState,
  GeneratorFormV2,
  useGeneratorFormState,
} from '../../../../components/_organisms/DesignerTabPane/PromptsTab/GeneratorForm';
import { ConsumerGenerator } from '../../../../components/dynamicPages/CreatePage/test-prep-tab/consumer-generator';
import { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import useConsumeGeneratorAsNewPresentation from '../../../../hooks/useConsumeGeneratorAsNewPresentation';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRoutePrefetch from '../../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import useSSRGeneratorNew from '../../../../hooks/useSSRGeneratorNew';
import { RouterHistoryContext } from '../../../../providers/RouterHistoryProvider/RouterHistoryProvider';
import backendService from '../../../../services/backendService';
import { PromptInput } from '../../../../services/backendService/types';
import CoreAPIService from '../../../../services/CoreAPIService';
import { GeneratorCoarseLicenseLevel } from '../../../../services/TemplateService/Template.type';
import { generatorFormStateToUserInputFormData } from '../../../../utils/aiGeneratorUtils';
import { subjectOptions } from '../../../../utils/authenticatedHomeUtils';
import { getBaseUrl } from '../../../../utils/client';
import { getActivityForSlideType } from '../../../../utils/creator';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../../utils/react-query-utils';
import { provisionFlags } from '../../../../utils/ServerSideFeatureFlagUtils';

const GeneratorPreviewContainer = styled.div`
  min-height: calc(100vh - 60px);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 60px;
  position: relative;
`;

const PageContainer = styled.div<{ $isAuthenticated?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

export const NewMainContainerWithGeneratorFormV2 = ({
  generator,
  authenticated,
  searchPhrase,
  grade,
  isFullLessonEmptyState,
}: {
  generator: ConsumerGenerator;
  authenticated: boolean;
  searchPhrase?: string;
  grade?: string;
  isFullLessonEmptyState?: boolean;
}) => {
  const { tenantId, router } = useRouterMatch();
  const metrics = useMetrics();
  const {
    consumeGenerator,
    isError,
    isLoading,
    data,
    error,
  } = useConsumeGeneratorAsNewPresentation(tenantId);

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generator.id,
  );

  const handleConsumeGenerator = useCallback(
    (formState: GeneratorFormState) => {
      if (!authenticated) {
        showAuthGuard();
        return;
      }

      //if isFullLessonEmptyState we log the event
      if (isFullLessonEmptyState) {
        metrics.logEvent('Generator.ConsumeGeneratorFromStandardBrowsing.fullLesson', {
          generatorId: generator.id,
          standard: searchPhrase,
          grade: grade,
          from: 'generatorPage',
        });
      }

      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        generator?.aiContent?.promptInputs || [],
      );
      consumeGenerator({
        generatorId: generator.id || '',
        userInputFormData,
        generatorName: generator.title || '',
        generatorPlan: generator?.aiContent?.generatorPlan,
        onSuccessFunction: template => {
          // redirect to the designer on desktop

          localStorage.setItem(
            '@curipod_v1_recentGeneratorConsume',
            JSON.stringify({
              generatorId: generator.id,
              formState,
            }),
          );

          router.push(
            `/${tenantId}/lessons/${template.id}/edit?generatorId=${generator.id}&activities=true`,
          );
        },
      });
    },
    [
      authenticated,
      isFullLessonEmptyState,
      generator?.aiContent?.promptInputs,
      generator?.aiContent?.generatorPlan,
      generator.id,
      generator.title,
      consumeGenerator,
      showAuthGuard,
      metrics,
      searchPhrase,
      grade,
      router,
      tenantId,
    ],
  );

  return (
    <>
      {authGuardComponent}
      <GeneratorFormContainerV2>
        {(isLoading || data) && (
          <LoaderContainerV2>
            <ProgressBarV2 isSmallTemplates />
          </LoaderContainerV2>
        )}
        {!(isLoading || data) && (
          <GeneratorFormBackground>
            <GeneratorsFormV2
              promptInputs={generator?.aiContent?.promptInputs || []}
              handleConsumeGenerator={handleConsumeGenerator}
              isError={isError}
              error={error}
              promptInputSuggestions={generator?.aiContent?.promptInputSuggestions || []}
              preFillSearch={searchPhrase}
              preFillGrade={grade}
              isLoading={isLoading}
              isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
              generatorPlan={generator?.aiContent?.generatorPlan}
              generatorId={generator?.id}
            />
          </GeneratorFormBackground>
        )}
      </GeneratorFormContainerV2>
    </>
  );
};

const GeneratorFormBackground = styled.div`
  padding: 20px;
  min-width: 340px;
  width: 90%;
  max-width: 580px;
`;

const LoaderContainerV2 = styled(GeneratorFormBackground)`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  padding: 20px 5px;
  overflow: hidden;
  background: transparent;
  padding: 20px 40px;
  border-radius: 10px;
`;

const GeneratorFormContainerV2 = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  height: 100%;
  position: relative;
  z-index: 1;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 10px;
  padding: 20px;
  min-width: 340px;
  width: 90%;
  max-width: 580px;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 5px;
  }
`;

const GeneratorsFormV2 = ({
  promptInputs,
  isLoading,
  isError,
  error,
  handleConsumeGenerator,
  promptInputSuggestions,
  formState: existingFormState,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  preFillSearch,
  preFillGrade,
}: {
  promptInputs: PromptInput[];
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  handleConsumeGenerator: (formState: GeneratorFormState) => void;
  formState?: GeneratorFormState;
  promptInputSuggestions?: PromptInput[][];
  isPremiumGenerator?: boolean | null;
  generatorPlan?: GeneratorCoarseLicenseLevel | null;
  generatorId?: string;
  preFillSearch?: string;
  preFillGrade?: string;
}) => {
  const metrics = useMetrics();
  const router = useRouter();
  const {
    handleInputChange,
    handleMultipleInputsChange,
    formState,
  } = useGeneratorFormState(promptInputs, existingFormState);

  const handleSubmit = useCallback(() => {
    //If the generator is consumed from browsing standards, log the event
    if (router.query.standardId) {
      metrics.logEvent('Generator.ConsumeGeneratorFromStandardBrowsing', {
        standardId: router.query.standardId as string,
        subjectSelected: router.query.subject as string,
        gradeSelected: router.query.grade as string,
        generatorId: generatorId,
        from: 'generatorPage',
      });
    }

    if (router.query.fromLessonCategory) {
      metrics.logEvent('Generator.ConsumeGeneratorFromCreateWithAI', {
        generatorId: generatorId,
        selectedLessonCategory: router.query.fromLessonCategory as string,
        selectedCategory: router.query.fromCategory as string,
        from: 'createPage',
      });
    }

    handleConsumeGenerator(formState);
  }, [
    formState,
    generatorId,
    handleConsumeGenerator,
    metrics,
    router.query.grade,
    router.query.standardId,
    router.query.subject,
    router.query.fromLessonCategory,
    router.query.fromCategory,
  ]);

  return (
    <GeneratorFormV2
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      promptInputSuggestions={promptInputSuggestions}
      promptInputs={promptInputs}
      formState={formState}
      isLoading={isLoading}
      isError={isError}
      error={error}
      isPremiumGenerator={isPremiumGenerator}
      generatorPlan={generatorPlan}
      generatorId={generatorId}
      upgradeLocation="GeneratorPage"
      preFillSearch={preFillSearch}
      preFillGrade={preFillGrade}
      handleMultipleInputsChange={handleMultipleInputsChange}
    />
  );
};

const NewGeneratorHead = () => {
  const { router } = useRouterMatch();
  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  if (!generator) return null;
  const description = `${generator.description} | A customizable lesson created by Curipod pedagogy experts`;
  return (
    <Head>
      <title>
        {generator.title} by {generator.creatorName} - Curipod
      </title>
      <meta
        property="og:title"
        content={`${generator.title} by ${generator.creatorName} - Curipod`}
        key="title"
      />
      <meta
        name="twitter:title"
        content={`${generator.title} by ${generator.creatorName} - Curipod`}
      />
      <meta name="description" content={description} key="namedescription" />
      <meta property="description" content={description} key="description" />
      <meta property="og:description" content={description} key="og:description" />
      <meta name="twitter:description" content={description} />
      <meta
        property="og:image"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
        key="imageurl"
      />
      <meta
        property="og:image:secure_url"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
        key="imagesecureurl"
      />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/svg+xml'} key="imagetype" />
      <meta
        property="og:image:alt"
        content={generator.description || ''}
        key="imageAlt"
      />
      <meta property="og:image:width" content={'160'} key="imageWidth" />
      <meta property="og:image:height" content={'108'} key="imageHeight" />
      <meta
        name="twitter:image"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
      />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const GeneratorPage = () => {
  const session = useSession();

  // Designer page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: true,
  });

  return (
    <PageContainer>
      <NewGeneratorHead />
      <NewGeneratorPreviewV2 authenticated={session.status === 'authenticated'} />
      <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
    </PageContainer>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const queryClient = new QueryClient();
  const session = await getSession({ req: context.req });

  const { generatorId } = context.query as { generatorId: string };
  const flags = await provisionFlags(context, queryClient, session);

  const generator = await queryClient.fetchQuery({
    queryKey: ['generator-retrieve', generatorId],

    queryFn: async () => {
      return CoreAPIService.fetchSingleGeneratorElastic(generatorId);
    },

    staleTime: 1000 * 60 * 5
  });

  // Check if generator exists and is published
  if (!generator || generator.status !== 'published') {
    return {
      redirect: {
        destination: '/old-generator/create', // Where old generator will be replaced with tenantId
        permanent: false,
      },
    };
  }

  return {
    props: {
      ...flags,
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
      session,
    },
  };
};

export default GeneratorPage;

const NewGeneratorPreviewV2 = ({ authenticated }: { authenticated: boolean }) => {
  const { router } = useRouterMatch();
  const ismobile = useIsMobile();
  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { authGuardComponent } = useAuthGuardPrompt(undefined, undefined, generator?.id);

  const { previousRoute } = useContext(RouterHistoryContext);

  const { userAccessBadge, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const generatorBackgroundImageUrl = useMemo(() => {
    if (!generator || !generator.aiContent?.backgroundImageId) return null;
    const backgroundUrl = backendService.instance.generatorPlugin.getGeneratorThumbnailUrl(
      generator.aiContent?.backgroundImageId,
    );
    return backgroundUrl;
  }, [generator]);

  const slideActivites = useMemo(() => {
    return uniq(
      generator?.slides
        ?.filter(
          s => s.type !== 'podium' && s.type !== 'responsegrid' && s.type !== 'slide',
        )
        ?.map(slide => {
          return getActivityForSlideType(slide.type)?.title;
        }) || [],
    ).filter(Boolean);
  }, [generator?.slides]);

  const handleExpandDescription = () => {
    if (isDescriptionExpanded) {
      setIsDescriptionExpanded(false);
      return;
    }
    setIsDescriptionExpanded(true);
  };

  const handleBackClick = () => {
    if (!previousRoute) {
      router.push(`/`);
      return;
    }
    router.back();
  };

  return (
    <GeneratorPreviewContainer style={{ paddingTop: 0 }}>
      {generator && (
        <>
          {authGuardComponent}
          {paywallComponent}
          <TopSection $backgroundImageUrl={generatorBackgroundImageUrl || ''}>
            <TopSectionContent>
              <TopSectionRow1>
                <BackContainer
                  onClick={() => {
                    handleBackClick();
                  }}
                >
                  <RightAngleSvg />
                  <StyledText>Back</StyledText>
                </BackContainer>

                <ShareContainer
                  onClick={e => {
                    setShowShareModal(true);
                    e.stopPropagation();
                  }}
                >
                  <IconWrapper
                    height="25px"
                    width="25px"
                    onClick={e => {
                      setShowShareModal(true);
                      e.stopPropagation();
                    }}
                    style={{
                      textDecoration: 'underline',
                      gap: 5,
                      color: theme.colors.white,
                    }}
                  >
                    <ShareFilledSvg />
                  </IconWrapper>

                  <StyledText>Share</StyledText>
                </ShareContainer>
              </TopSectionRow1>
              <TopSectionRow2>
                <div
                  style={{
                    margin: 'auto 0px',
                    gap: 20,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Title $fontSize={48}>{generator.title}</Title>

                  <GetLessonButton
                    styles={
                      ismobile
                        ? {
                            width: 'fit-content',
                          }
                        : {}
                    }
                    onClick={() => {
                      const element = document.getElementById('get-lesson');
                      if (!element) return;
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'start',
                      });
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 10,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper height="30px" width="30px">
                        <CuripodAIStarsSvg />
                      </IconWrapper>

                      <span>Generate Lesson</span>
                    </div>
                  </GetLessonButton>
                </div>
              </TopSectionRow2>
              <TopSectionRow3>
                {generator.aiContent?.generatorPlan ===
                  GeneratorCoarseLicenseLevel.enterprise ||
                generator.aiContent?.generatorPlan ===
                  GeneratorCoarseLicenseLevel.premium ? (
                  <Tooltip
                    title={
                      userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.premium ||
                      userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.enterprise
                        ? `Available to you as you're on the school plan`
                        : 'This lesson is accessible on the school plan'
                    }
                  >
                    <div
                      style={{ display: 'flex', cursor: 'pointer' }}
                      onClick={() => {
                        if (
                          userAccessBadge?.license.coarseLevel !==
                            LicenseCoarseLevels.premium &&
                          userAccessBadge?.license.coarseLevel !==
                            LicenseCoarseLevels.enterprise
                        ) {
                          promptPaywall({
                            label: 'Upgrade account',
                            description: `Generator: ${generator?.title}`,
                          });
                        }
                      }}
                    >
                      <IconWrapper
                        height="25px"
                        width="25px"
                        style={{ marginRight: 10 }}
                        color={
                          userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.premium ||
                          userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.enterprise
                            ? theme.colors.white
                            : theme.colors.yellow
                        }
                      >
                        <EnterprisePlanIcon />
                      </IconWrapper>
                      <StyledTextSaveToCollection>
                        <T keyName="PremiumLesson" defaultValue="Premium Lesson" />
                      </StyledTextSaveToCollection>
                    </div>
                  </Tooltip>
                ) : (
                  <div />
                )}
              </TopSectionRow3>
            </TopSectionContent>
          </TopSection>

          <DetailSection>
            <DetailSectionLeft>
              <Title $fontSize={32} style={{ color: theme.colors.black }}>
                <T keyName="Description" defaultValue="Description" />
              </Title>

              <DescriptionText>
                {isDescriptionExpanded ||
                (generator?.description && generator?.description?.length < 500)
                  ? generator.description
                  : `${generator?.description?.slice(0, 500)}...`}
              </DescriptionText>
              {generator?.description && generator?.description?.length > 500 && (
                <Button
                  onClick={handleExpandDescription}
                  type="ghost"
                  styles={{ width: 'fit-content', border: 'none', alignSelf: 'flex-end' }}
                  icon={
                    <IconWrapper
                      height="25px"
                      width="25px"
                      style={{
                        transform: isDescriptionExpanded
                          ? 'rotate(90deg)'
                          : 'rotate(-90deg)',
                      }}
                    >
                      <ArrowLeftSvg />
                    </IconWrapper>
                  }
                >
                  {isDescriptionExpanded ? (
                    <T keyName="SeeLess" defaultValue="See less" />
                  ) : (
                    <T keyName="SeeMore" defaultValue="See more" />
                  )}
                </Button>
              )}
            </DetailSectionLeft>
            <DetailSectionRight>
              {!!generator.subjects.length && (
                <DetailsGroup>
                  <DetailsRightTitle>SUBJECTS</DetailsRightTitle>
                  <DetailsChipContainer>
                    {generator.subjects.map(subject => (
                      <DetailsChip key={subject}>
                        <DetailsChipDarkText>
                          {
                            subjectOptions.find(option => option.value === subject)
                              ?.displayName
                          }
                        </DetailsChipDarkText>
                      </DetailsChip>
                    ))}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!generator.grades.length && (
                <DetailsGroup>
                  <DetailsRightTitle>GRADES</DetailsRightTitle>
                  <DetailsChipContainer>
                    {groupedGrades(generator.grades)}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!slideActivites.length && (
                <DetailsGroup>
                  <DetailsRightTitle>INTERACTIVE ACTIVITIES</DetailsRightTitle>
                  <DetailsChipContainer>
                    {slideActivites.map((activity, index) => (
                      <DetailsChip key={index}>
                        <DetailsChipDarkText>{activity}</DetailsChipDarkText>
                      </DetailsChip>
                    ))}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!generator.aiContent?.durationInMins && (
                <DetailsGroup>
                  <DetailsRightTitle>DURATION</DetailsRightTitle>
                  <DetailsChipContainer>
                    <DetailsChip>
                      <DetailsChipDarkText>
                        <ClockSvg /> {generator.aiContent?.durationInMins} minute
                        {generator.aiContent?.durationInMins === 1 ? '' : 's'}
                      </DetailsChipDarkText>
                    </DetailsChip>
                  </DetailsChipContainer>
                </DetailsGroup>
              )}

              {!!generator?.learningStandards?.length && (
                <DetailsGroup>
                  <DetailsRightTitle>STANDARDS</DetailsRightTitle>
                  <DetailsChipContainer>
                    {generator?.learningStandards?.map(standard => (
                      <DetailsChip key={standard.id}>
                        <DetailsChipDarkText>{standard.label}</DetailsChipDarkText>
                      </DetailsChip>
                    ))}
                  </DetailsChipContainer>
                </DetailsGroup>
              )}
            </DetailSectionRight>
          </DetailSection>

          <CustomizeSection $backgroundImageUrl={generatorBackgroundImageUrl || ''}>
            <CustomizeSectionContent>
              <CustomizeTitle id="get-lesson">
                Customize into your own lesson
              </CustomizeTitle>
              <NewMainContainerWithGeneratorFormV2
                authenticated={authenticated}
                generator={generator}
              />
            </CustomizeSectionContent>
          </CustomizeSection>

          <BaseModal
            visible={showShareModal}
            onCancel={() => setShowShareModal(false)}
            title={'Share this customizable lesson'}
          >
            <ShareGlobalLesson
              templateId={generator.id}
              isGeneratedByAI={false}
              isGenerator
              title={generator.title || ''}
            />
          </BaseModal>
        </>
      )}
    </GeneratorPreviewContainer>
  );
};

const groupedGrades = (grades: string[]) => {
  const formattedGrade = (grade: number) => {
    switch (grade) {
      case 0: // Kindergarten
        return 'Kindergarten';
      case 1:
        return '1st';
      case 2:
        return '2nd';
      case 3:
        return '3rd';
      default:
        return grade + 'th';
    }
  };

  const sortedGrades = grades.sort((a, b) => parseInt(a) - parseInt(b));
  const groupedGrades = [];
  let start = 0;
  let end = 0;

  for (let i = 0; i <= sortedGrades.length; i++) {
    if (i === 0) {
      start = parseInt(sortedGrades[i]);
      end = start;
      continue;
    }
    if (parseInt(sortedGrades[i]) - end === 1) {
      end = parseInt(sortedGrades[i]);
    } else {
      if (start === end) {
        groupedGrades.push(formattedGrade(start));
      } else {
        groupedGrades.push(`${formattedGrade(start)}-${formattedGrade(end)}`);
      }
      start = parseInt(sortedGrades[i]);
      end = start;
    }
  }

  return (
    <>
      {groupedGrades.map(grade => (
        <DetailsChip key={grade}>
          <DetailsChipDarkText>
            <CapSvg /> {grade}
          </DetailsChipDarkText>
        </DetailsChip>
      ))}
    </>
  );
};

const TopSection = styled.div<{ $backgroundImageUrl: string }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  background-image: url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
  min-height: calc(80vh - 60px);
  max-height: fit-content;
  height: fit-content;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: row;
  padding: 40px 80px;
  gap: 50px;
  width: 100%;
  max-width: 1400px;
  margin: auto;

  @media (max-width: 767px) {
    padding: 40px 20px;
    flex-direction: column-reverse;
  }
`;

const DetailSectionLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 50%;

  @media (max-width: 1000px) {
    width: 100%;
  }

  button {
    display: flex;
    flex-direction: row-reverse;
    box-shadow: none;

    svg {
      margin: 0;
    }

    &:hover,
    &:focus,
    &:active {
      box-shadow: none;
      background: none;
    }
  }
`;

const DetailSectionRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 8px solid black;
  border-radius: 5px;
  padding: 30px;
  gap: 30px;
  width: 50%;

  @media (max-width: 1000px) {
    width: 100%;
  }
`;

const CustomizeSection = styled.div<{ $backgroundImageUrl: string }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: calc(70vh - 60px);
  background-image: url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
`;

const TopSectionContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  padding: 40px 80px;
  flex-direction: column;
  background-color: rgba(0, 0, 0, 0.6); // Black overlay with 0.6 opacity
  height: 100%;
  gap: 20px;

  @media (max-width: 767px) {
    padding: 40px 20px;
  }
`;

const CustomizeSectionContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  padding: 40px 80px;
  flex-direction: column;
  gap: 20px;
  background-color: rgba(0, 0, 0, 0.6); // Black overlay with 0.6 opacity
  height: 100%;
  justify-content: center;
  align-items: center;

  @media (max-width: 767px) {
    padding: 40px 20px;
  }

    button {
  background: ${theme.colors.white};
  color: ${theme.colors.black};

  &:hover, &:focus, &:active {
    background: ${theme.colors.grey};
    color: ${theme.colors.black};
  }
`;

const TopSectionRow1 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const TopSectionRow2 = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: auto 0px;
  height: fit-content;
  flex-grow: 1;
  width: 100%;
  max-width: 500px;

  @media (max-width: 1000px) {
    max-width: 80%;
  }
  @media (max-width: 860px) or (max-height: 860px) {
    max-width: 50%;
  }

  button {
  background: ${theme.colors.white};
  color: ${theme.colors.black};

  &:hover, &:focus, &:active {
    background: ${theme.colors.grey};
    color: ${theme.colors.black};
  }
`;

const TopSectionRow3 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledText = styled.p`
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  dixplay: flex;
  align-items: center;
  margin: 0px;
`;

const StyledTextSaveToCollection = styled.p`
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  dixplay: flex;
  align-items: center;
  margin: 0px;
  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const BackContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ShareContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: 800;
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  margin: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 10}px;
  }
`;

const DescriptionText = styled.p`
  color: #000;

  font-family: Nunito;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 136.952%;
  white-space: pre-wrap;

  @media (max-width: 800px) {
    font-size: 18px;
  }
`;

const DetailsRightTitle = styled.text`
  color: #565760;

  font-family: Nunito;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const DetailsGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailsChip = styled.div`
  align-self: flex-start;
  margin-top: 10px;

  display: flex;
  padding: 2px 10px;
  justify-content: center;
  align-items: center;
  gap: 5px;

  border-radius: 20px;
  background: #fff;

  /* Barely floating */
  box-shadow: 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13),
    0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11);
`;

const DetailsChipDarkText = styled.text`
  color: #000;

  font-family: Nunito;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const DetailsChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
`;

const CustomizeTitle = styled.text`
  color: #fff;

  font-family: Nunito;
  font-size: 48px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  @media (max-width: 800px) {
    font-size: 28px;
    text-align: center;
  }
`;

const GetLessonButton = styled(Button)`
  width: fit-content;
  min-height: 60px;
  min-width: 200px;
  font-size: 24px;
  font-weight: bolder;
  background-color: #f99746 !important;
  color: white !important;
  svg {
    height: 30px;
    width: 30px;
    margin-right: 0px;
  }
  @media (max-width: 767px) {
  }
`;
